/**
 * Secrets Collective — cart drawer cross-sell.
 *
 * The rule matching happens in Liquid; this only handles size selection and
 * the add. Colour is inherited from the item that triggered the promo, so the
 * customer picks one thing rather than two.
 */

import { addItems } from './sc-bundle.js';

class SCCartPromo extends HTMLElement {
  /*
   * The cart drawer is morphed, not replaced. This element survives a cart
   * update while its children are swapped for fresh server markup — so any
   * listener bound to a child button dies, connectedCallback never fires again
   * (the host was never disconnected), and the card looks alive but does
   * nothing until a full page load.
   *
   * Two consequences, both deliberate:
   *   1. Clicks are delegated to the host, which survives the morph.
   *   2. Child elements are looked up when needed, never cached in fields,
   *      because a cached node reference points at a node that is no longer
   *      in the document.
   *
   * The bound flag is a plain field, not dataset — dataset writes a real
   * attribute, and that attribute survives the morph too, which is what made
   * the old guard block the rebind it was supposed to allow.
   */
  #bound = false;

  connectedCallback() {
    this.variants = this.#json('variants');
    this.sizeIndex = Number(this.dataset.sizeIndex) || 0;
    this.colourIndex = Number(this.dataset.colourIndex) || 0;
    this.colour = this.dataset.colour || '';
    this.saving = Number(this.dataset.savingCents) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.ctaLabel = this.dataset.cta || 'Add';
    this.chooseLabel = this.chooseLabel || this.ctaText?.textContent?.trim() || 'Select a size';

    if (!this.#bound) {
      this.#bound = true;
      this.addEventListener('click', this.#onClick);
    }

    this.#update();
  }

  get cta() {
    return this.querySelector('[ref="cta"]');
  }
  get ctaText() {
    return this.querySelector('[ref="ctaText"]');
  }
  get msg() {
    return this.querySelector('[ref="msg"]');
  }
  get wasPrice() {
    return this.querySelector('[ref="wasPrice"]');
  }
  get nowPrice() {
    return this.querySelector('[ref="nowPrice"]');
  }
  get savingLabel() {
    return this.querySelector('[ref="savingLabel"]');
  }

  #onClick = (event) => {
    const size = event.target.closest('[data-promo-size]');
    if (size && this.contains(size)) {
      if (size.disabled) return;
      for (const o of this.querySelectorAll('[data-promo-size]')) {
        o.setAttribute('aria-pressed', String(o === size));
      }
      this.selectedSize = size.dataset.value;
      this.#update();
      return;
    }

    const cta = event.target.closest('[ref="cta"]');
    if (cta && this.contains(cta)) this.#submit();
  };

  get selectedSize() {
    return this.querySelector('[data-promo-size][aria-pressed="true"]')?.dataset.value || null;
  }

  set selectedSize(_v) {
    /* derived from the DOM, kept as a setter so existing assignments are safe */
  }

  #json(ref) {
    const el = this.querySelector(`[ref="${ref}"]`);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch {
      return [];
    }
  }

  #matchesColour(v) {
    if (!this.colour || !this.colourIndex) return true;
    return v.options[this.colourIndex - 1] === this.colour;
  }

  #variant() {
    if (!this.selectedSize) return null;
    return (
      this.variants.find(
        (v) => v.options[this.sizeIndex - 1] === this.selectedSize && this.#matchesColour(v)
      ) || null
    );
  }

  #update() {
    const fmt = (c) => money(c, this.moneyFormat);

    this.querySelectorAll('[data-promo-size]').forEach((btn) => {
      const exists = this.variants.some(
        (v) => v.available && v.options[this.sizeIndex - 1] === btn.dataset.value && this.#matchesColour(v)
      );
      btn.disabled = !exists;
      btn.classList.toggle('is-unavailable', !exists);
    });

    if (this.savingLabel && this.saving > 0) {
      this.savingLabel.textContent = `Save ${fmt(this.saving)}`;
    }

    const variant = this.#variant();
    if (variant) {
      const now = Math.max(0, variant.price - this.saving);
      if (this.wasPrice) this.wasPrice.textContent = fmt(variant.price);
      if (this.nowPrice) this.nowPrice.textContent = fmt(now);
    }

    const ready = Boolean(variant);
    if (this.cta) this.cta.disabled = !ready;
    if (this.ctaText) {
      this.ctaText.textContent = ready
        ? `${this.ctaLabel} — ${fmt(Math.max(0, variant.price - this.saving))}`
        : this.chooseLabel;
    }
  }

  async #submit() {
    const variant = this.#variant();
    if (!variant || this.cta.disabled) return;

    this.cta.disabled = true;
    this.ctaText.textContent = 'Adding…';
    this.msg?.classList.remove('is-error');

    const result = await addItems(this, [{ id: variant.id, quantity: 1 }]);

    if (!result.ok) {
      this.cta.disabled = false;
      this.#update();
      if (this.msg) {
        this.msg.textContent = result.message || 'Could not add to cart';
        this.msg.classList.add('is-error');
      }
    }
    // On success the drawer re-renders from the server, which re-evaluates the
    // promo rules — this card disappears on its own.
  }
}

function money(cents, format) {
  const amount = cents / 100;
  const plain = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const withCommas = plain.replace(/\B(?=(\d{3})+(?!\d)(?!\.))/g, ',');
  if (typeof format === 'string' && format.includes('{{')) {
    return format.replace(/\{\{\s*[a-z_]+\s*\}\}/g, withCommas);
  }
  return `$${withCommas}`;
}

if (!customElements.get('sc-cart-promo')) {
  customElements.define('sc-cart-promo', SCCartPromo);
}
